import React, { useState, useEffect } from 'react';
import { ChevronDown, Building2, Check, Plus, Crown, Users, AlertTriangle, Loader2, Smartphone, Wifi, WifiOff, Link } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { maskCNPJ, cleanCNPJ, validateCNPJ } from '../utils/brazilValidation';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ZAPIStatusIndicator - Shows WhatsApp connection status
 */
const ZAPIStatusIndicator = ({ status, phone }) => {
  if (!status) return null;
  
  const statusConfig = {
    connected: {
      icon: Wifi,
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      label: phone || 'Conectado'
    },
    disconnected: {
      icon: WifiOff,
      color: 'text-amber-500',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      label: 'Desconectado'
    },
    inherited: {
      icon: Link,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      label: phone || 'Herdado'
    },
    not_configured: {
      icon: Smartphone,
      color: 'text-slate-400',
      bgColor: 'bg-slate-50',
      borderColor: 'border-slate-200',
      label: 'Não configurado'
    }
  };
  
  const config = statusConfig[status] || statusConfig.not_configured;
  const Icon = config.icon;
  
  return (
    <div className={`flex items-center gap-1.5 mt-1 px-2 py-0.5 rounded-full text-xs ${config.bgColor} ${config.borderColor} border`}>
      <Icon className={`h-3 w-3 ${config.color}`} />
      <span className={`${config.color} font-medium truncate max-w-[120px]`}>
        {config.label}
      </span>
    </div>
  );
};

export default function CompanySwitcher() {
  const { isMaster, managedCompanies, activeCompany, switchCompany, refreshCompanies, user } = useAuth();
  const [switching, setSwitching] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingCompany, setAddingCompany] = useState(false);
  const [newCompany, setNewCompany] = useState({
    nome: '',
    cnpj: '',
    razao_social: '',
    endereco: '',
    telefone: '',
    email: ''
  });
  const [cnpjError, setCnpjError] = useState('');
  
  // Z-API Status state
  const [zapiStatus, setZapiStatus] = useState({});
  const [loadingZapiStatus, setLoadingZapiStatus] = useState(false);

  // Fetch Z-API status when dropdown opens or companies change
  const fetchZapiStatus = async () => {
    if (!isMaster) return;
    
    setLoadingZapiStatus(true);
    try {
      const response = await axios.get(`${API}/api/master/companies/zapi-status`);
      if (response.data?.zapi_status) {
        setZapiStatus(response.data.zapi_status);
      }
    } catch (error) {
      console.error('Error fetching Z-API status:', error);
    } finally {
      setLoadingZapiStatus(false);
    }
  };

  // Fetch status when managedCompanies change
  useEffect(() => {
    if (isMaster && managedCompanies.length > 0) {
      fetchZapiStatus();
    }
  }, [isMaster, managedCompanies.length]);

  // If not Master plan, don't show the switcher
  if (!isMaster) {
    return null;
  }

  const handleSwitchCompany = async (companyId) => {
    if (activeCompany?.id === companyId) return;
    
    setSwitching(true);
    try {
      await switchCompany(companyId);
      toast.success('Empresa alterada com sucesso!');
    } catch (error) {
      console.error('Error switching company:', error);
      toast.error('Erro ao trocar de empresa');
    } finally {
      setSwitching(false);
    }
  };

  const handleCNPJChange = (value) => {
    const masked = maskCNPJ(value);
    setNewCompany(prev => ({ ...prev, cnpj: masked }));
    
    const cleaned = cleanCNPJ(value);
    if (cleaned.length === 14) {
      if (!validateCNPJ(cleaned)) {
        setCnpjError('CNPJ inválido');
      } else {
        setCnpjError('');
      }
    } else {
      setCnpjError('');
    }
  };

  const handleAddCompany = async () => {
    // Validations
    if (!newCompany.nome.trim()) {
      toast.error('Informe o nome da empresa');
      return;
    }
    
    if (!newCompany.cnpj || cleanCNPJ(newCompany.cnpj).length !== 14) {
      toast.error('Informe um CNPJ válido');
      return;
    }
    
    if (!validateCNPJ(newCompany.cnpj)) {
      toast.error('CNPJ inválido');
      return;
    }

    setAddingCompany(true);
    try {
      const response = await axios.post(`${API}/api/master/companies`, {
        nome: newCompany.nome,
        cnpj: cleanCNPJ(newCompany.cnpj),
        razao_social: newCompany.razao_social || newCompany.nome,
        endereco: newCompany.endereco,
        telefone: newCompany.telefone,
        email: newCompany.email
      });

      if (response.data) {
        toast.success(response.data.message || 'Empresa criada com sucesso!');
        setShowAddModal(false);
        setNewCompany({
          nome: '',
          cnpj: '',
          razao_social: '',
          endereco: '',
          telefone: '',
          email: ''
        });
        // Refresh the companies list
        await refreshCompanies();
        // Refresh Z-API status
        fetchZapiStatus();
      }
    } catch (error) {
      console.error('Error adding company:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar empresa');
    } finally {
      setAddingCompany(false);
    }
  };

  // Get Z-API status for active company
  const activeZapiStatus = activeCompany ? zapiStatus[activeCompany.id] : null;

  return (
    <>
      <DropdownMenu onOpenChange={(open) => open && fetchZapiStatus()}>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            className="flex items-center gap-2 border-[#0d9488]/30 bg-gradient-to-r from-[#0d9488]/5 to-transparent hover:from-[#0d9488]/10 hover:border-[#0d9488]/50"
            data-testid="company-switcher-trigger"
          >
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#1e3a5f] to-[#0d9488] flex items-center justify-center">
              <Building2 className="h-4 w-4 text-white" />
            </div>
            <div className="flex flex-col items-start max-w-[150px]">
              <span className="text-xs text-[#64748b] font-normal">Empresa Ativa</span>
              <span className="text-sm font-medium text-[#1e3a5f] truncate w-full">
                {activeCompany?.nome || 'Selecionar'}
              </span>
            </div>
            {/* Z-API status indicator for active company */}
            {activeZapiStatus && (
              <div className={`w-2 h-2 rounded-full ${
                activeZapiStatus.status === 'connected' ? 'bg-emerald-500' :
                activeZapiStatus.status === 'inherited' ? 'bg-blue-500' :
                activeZapiStatus.status === 'disconnected' ? 'bg-amber-500' :
                'bg-slate-300'
              }`} title={`WhatsApp: ${activeZapiStatus.status}`} />
            )}
            {switching ? (
              <Loader2 className="h-4 w-4 animate-spin text-[#0d9488]" />
            ) : (
              <ChevronDown className="h-4 w-4 text-[#64748b]" />
            )}
          </Button>
        </DropdownMenuTrigger>
        
        <DropdownMenuContent align="start" className="w-80" data-testid="company-switcher-menu">
          <DropdownMenuLabel className="flex items-center justify-between text-[#1e3a5f]">
            <div className="flex items-center gap-2">
              <Crown className="h-4 w-4 text-amber-500" />
              Plano Master - Multi-CNPJ
            </div>
            {loadingZapiStatus && (
              <Loader2 className="h-3 w-3 animate-spin text-slate-400" />
            )}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {managedCompanies.length === 0 ? (
            <div className="px-2 py-4 text-center text-sm text-gray-500">
              <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-amber-400" />
              <p>Nenhuma empresa cadastrada</p>
            </div>
          ) : (
            managedCompanies.map((company) => {
              const companyZapi = zapiStatus[company.id];
              
              return (
                <DropdownMenuItem
                  key={company.id}
                  className={`flex items-center justify-between cursor-pointer py-3 ${
                    activeCompany?.id === company.id ? 'bg-[#e8f4fc]' : ''
                  }`}
                  onClick={() => handleSwitchCompany(company.id)}
                  data-testid={`company-option-${company.id}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      company.is_parent 
                        ? 'bg-gradient-to-br from-amber-400 to-amber-600' 
                        : 'bg-gradient-to-br from-[#1e3a5f] to-[#0d9488]'
                    }`}>
                      <Building2 className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex flex-col min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-[#1e3a5f] truncate">
                          {company.nome}
                        </span>
                        {company.is_parent && (
                          <Badge className="bg-amber-100 text-amber-700 text-[10px] py-0 flex-shrink-0">
                            Principal
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">{company.cnpj}</span>
                      
                      {/* Stats row */}
                      {company.stats && (
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {company.stats.employees}
                          </span>
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            {company.stats.risks}
                          </span>
                        </div>
                      )}
                      
                      {/* Z-API Status */}
                      <ZAPIStatusIndicator 
                        status={companyZapi?.status} 
                        phone={companyZapi?.connected_phone}
                      />
                    </div>
                  </div>
                  {activeCompany?.id === company.id && (
                    <Check className="h-4 w-4 text-[#0d9488] flex-shrink-0" />
                  )}
                </DropdownMenuItem>
              );
            })
          )}
          
          {user?.role === 'admin' && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="flex items-center gap-2 text-[#0d9488] cursor-pointer"
                onClick={() => setShowAddModal(true)}
                data-testid="add-company-button"
              >
                <Plus className="h-4 w-4" />
                Adicionar Nova Empresa
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Add Company Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-[#0d9488]" />
              Adicionar Nova Empresa
            </DialogTitle>
            <DialogDescription>
              Cadastre uma nova empresa vinculada ao seu plano Master.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="company-name">Nome da Empresa *</Label>
              <Input
                id="company-name"
                value={newCompany.nome}
                onChange={(e) => setNewCompany(prev => ({ ...prev, nome: e.target.value }))}
                placeholder="Nome fantasia"
              />
            </div>

            <div>
              <Label htmlFor="company-cnpj">CNPJ *</Label>
              <Input
                id="company-cnpj"
                value={newCompany.cnpj}
                onChange={(e) => handleCNPJChange(e.target.value)}
                placeholder="00.000.000/0000-00"
                className={cnpjError ? 'border-red-500' : ''}
                maxLength={18}
              />
              {cnpjError && (
                <p className="text-sm text-red-500 mt-1">{cnpjError}</p>
              )}
              {!cnpjError && newCompany.cnpj && newCompany.cnpj.replace(/\D/g, '').length === 14 && (
                <p className="text-sm text-green-600 mt-1">CNPJ válido</p>
              )}
            </div>

            <div>
              <Label htmlFor="company-razao">Razão Social</Label>
              <Input
                id="company-razao"
                value={newCompany.razao_social}
                onChange={(e) => setNewCompany(prev => ({ ...prev, razao_social: e.target.value }))}
                placeholder="Razão Social"
              />
            </div>

            <div>
              <Label htmlFor="company-email">E-mail</Label>
              <Input
                id="company-email"
                type="email"
                value={newCompany.email}
                onChange={(e) => setNewCompany(prev => ({ ...prev, email: e.target.value }))}
                placeholder="contato@empresa.com.br"
              />
            </div>

            <div>
              <Label htmlFor="company-telefone">Telefone</Label>
              <Input
                id="company-telefone"
                value={newCompany.telefone}
                onChange={(e) => setNewCompany(prev => ({ ...prev, telefone: e.target.value }))}
                placeholder="(11) 99999-9999"
              />
            </div>

            <div>
              <Label htmlFor="company-endereco">Endereço</Label>
              <Input
                id="company-endereco"
                value={newCompany.endereco}
                onChange={(e) => setNewCompany(prev => ({ ...prev, endereco: e.target.value }))}
                placeholder="Rua, número, cidade - UF"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleAddCompany}
              disabled={addingCompany}
              className="bg-[#0d9488] hover:bg-[#0f766e]"
            >
              {addingCompany ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Criando...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Empresa
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
